import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';

import { DirectionService } from '../../direction.service';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { UsersService } from 'src/app/super-management/user/users.service';
import { GeneralManagementService } from 'src/app/super-management/general-management.service';
import { ErrorComponent } from 'src/app/error/error.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-message-to-customer-view',
  templateUrl: './message-to-customer-view.component.html',
  styleUrls: ['./message-to-customer-view.component.css'],
  host: {
    class: 'fill-screen-modal-phone'
  }
})
export class MessageToCustomerViewComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;

  userId: string;
  userIsAuthenticated = false;
  private authStatusSub: Subscription;

  // Message data passed from parent component
  messageToCustomer: any = null;
  state: string = 'messageToCustomer';

  // Phone form
  form: FormGroup;
  userPhone: number = null;

  // Data from parent for joining distribution list
  printingService: string = null;
  branchOrPrinterId: string = null;

  // Success state
  isSuccess: boolean = false;
  successPhoneNumber: string = '';
  isLoading: boolean = false;

  constructor(
    private directionService: DirectionService,
    private authService: AuthService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private usersService: UsersService,
    private generalManagementService: GeneralManagementService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  async ngOnInit() {
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    this.userId = this.authService.getUserId();
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe(isAuthenticated => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
      });

    // Receive message data and state from parent component
    if (this.data) {
      this.messageToCustomer = this.data.messageToCustomer;
      this.state = this.data.state || 'messageToCustomer';
      this.printingService = this.data.printingService;
      this.branchOrPrinterId = this.data.branchOrPrinterId;
    }

    // Initialize phone form
    this.form = new FormGroup({
      phone: new FormControl(null, {
        validators: [this.phoneValidator]
      })
    });

    // Get user phone if available
    if (this.userId) {
      this.usersService.getUser(this.userId).subscribe((user: any) => {
        if (user && user.phone) {
          this.userPhone = user.phone;
          this.form.patchValue({
            phone: this.formatPhoneNumber(user.phone)
          });
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.authStatusSub) {
      this.authStatusSub.unsubscribe();
    }
  }

  closeMessageToCustomerViewDialog() {
    // ביטול/סגירה בלי הצטרפות – שמירת זמן נוכחי (הכפתור יופיע שוב אחרי 48 שעות)
    if (this.state === 'messageToHelpPt' && !this.isSuccess) {
      localStorage.setItem('messageToHelpPt', String(Date.now()));
    }
    this.dialogService.onCloseMessageToCustomerViewDialog();
  }

  openWhatsApp() {
    const phoneNumber = '97233746962';
    const message = encodeURIComponent('Eazix-Message');
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, '_blank');
  }

  // Join distribution list (for help and get points)
  joinDistributionList(): void {
    if (this.form.invalid || !this.printingService || !this.branchOrPrinterId || !this.userId) {
      return;
    }

    this.isLoading = true;

    // Extract phone number (remove dashes and leading 0, convert to number)
    const phoneString = this.form.value.phone.replace(/\D/g, '');
    const phoneNumber = parseInt(phoneString.replace(/^0+/, ''), 10);

    this.generalManagementService.joinHelpersDistributionList(
      this.printingService,
      this.branchOrPrinterId,
      this.userId,
      phoneNumber
    ).subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response.success) {
          // הצטרף לקבוצה – הכפתור לא יופיע שוב (dismissed לצמיתות)
          localStorage.setItem('messageToHelpPt', 'dismissed');
          this.isSuccess = true;
          this.successPhoneNumber = this.form.value.phone; // Keep formatted phone with 0
        } else {
          // Error response - ErrorInterceptor will handle HTTP errors automatically
          // If backend returns success:false (not HTTP error), ErrorInterceptor won't catch it
          // But since backend likely returns HTTP errors, we let ErrorInterceptor handle it
          // No need to call openErrorDialog here - it causes duplicate dialogs
        }
      },
      (error: any) => {
        this.isLoading = false;
        console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
        // ErrorInterceptor will automatically open the error dialog for HTTP errors
        // No need to call openErrorDialog here - it causes duplicate dialogs
      }
    );
  }

  // Open error dialog
  openErrorDialog(errorMessage: string): void {
    this.dialogService.onOpenErrorDialog(
      { message: errorMessage },
      { panelClass: 'zx-phone-dialog' },
    );
  }

  // ביטול הצטרפות – הכפתור יופיע שוב אחרי 48 שעות (זמן נוכחי ב־localStorage)
  unsubscribeFromDistributionList(): void {
    localStorage.setItem('messageToHelpPt', String(Date.now()));
    this.dialogService.onCloseMessageToCustomerViewDialog();
  }

  // Format phone number
  formatPhoneNumber(phoneNumber: number): string {
    if (!phoneNumber) {
      return '';
    }
    const str = '0' + phoneNumber.toString();
    return str.slice(0, 3) + '-' + str.slice(3, 7) + '-' + str.slice(7);
  }

  // Validate phone
  phoneValidator(control: AbstractControl): { [key: string]: any } | null {
    const valid = control.value && typeof control.value === 'string' && control.value.replace(/\D/g, '').length === 10;
    return valid ? null : { invalidPhone: { valid: false, value: control.value } };
  }

  // Check if phone is valid
  isPhoneValid(): boolean {
    return this.form && this.form.get('phone') && this.form.get('phone').valid;
  }
}

